/**
 * The half of the help file SirCAD writes for itself.
 *
 * {@link HELP_TOPICS} is extracted from the original's own context file and is
 * not edited — it is the record of what CIRCAD does, re-generated whenever the
 * extractor runs. Three things it cannot supply:
 *
 *   · commands the original never had, which have no topic to extract at all;
 *   · commands whose letters mean something else here, where the extracted
 *     paragraph describes a different program's command and is worse than
 *     nothing — `FE` erases a file from disk there and starts a blank drawing
 *     here;
 *   · commands whose behaviour here is wider than the original's, where the
 *     extracted words are true as far as they go and stop well short. Save is
 *     the plain case: there it wrote a file, and here a design lives in an
 *     account and a file is one of three ways out.
 *
 * So this file holds whole topics for the first two and extra paragraphs for
 * the third, and {@link ALL_TOPICS} is the merge. Where the original's words
 * still hold they are kept and followed, never rewritten.
 *
 * House style, because a help file that answers differently each time is
 * harder to read than a terse one:
 *
 *   · Say what the command is *for* before saying how to work it.
 *   · Where a command takes more than one action to finish, number the steps.
 *     A paragraph of numbered lines renders as a list.
 *   · Where there is another way to the same end, say so under "Also".
 *   · Where there is a choice to make, say what the choices are and which is
 *     the ordinary answer.
 *   · A line beginning `· ` is a bullet, and renders as one.
 */

import { HELP_TOPICS, type HelpTopic } from './help.ts';

// The merged file is the one everything reads, so the shape of a topic is
// re-exported from here too and nobody has to import from both.
export type { HelpTopic } from './help.ts';

/**
 * Whole topics: ours alone, or replacing an original that describes something
 * else.
 *
 * Exported so a check can hold them to a higher bar than the extracted ones —
 * they are written to a house style and there is nobody else to blame.
 *
 * Keyed by the *original's* code where there is one, since that is what the
 * extracted topics are keyed by and what the recoding maps back to.
 */
export const SIRCAD_TOPICS: HelpTopic[] = [
  // --- File: the design, the web and the disk --------------------------------
  {
    code: 'FS',
    title: 'Save',
    tip: 'Save the whole design — every sheet, the board and any text files — to your account.',
    body:
      'Saves the whole design to your account: every sheet, the board, and any '
      + 'text files open beside them. One command for the lot, because they are '
      + 'one design.\n\n'
      + 'There are three places work can go, and they are for different '
      + 'things:\n\n'
      + '· File ▸ Save (F S) — to the web: the project, in your account, '
      + 'reachable from any machine you sign in on. This is the ordinary one.\n'
      + '· File ▸ Save As… (file) (F A) — to a file: the whole design, as a '
      + '.sircad file on this machine. For sending somebody a copy, or keeping '
      + 'one outside the account. It loses nothing.\n'
      + '· File ▸ Save to Project (web)… (F F) — to the web under a new name: it '
      + 'makes a project from what is open. This is what Save does the first '
      + 'time, when there is no project yet to save into.\n\n'
      + 'Then there is export, which is a different thing again: File ▸ Gerber '
      + 'Out for the board house, File ▸ Export to KiCad for another program, '
      + 'File ▸ BOM Out and Nets ▸ Net List Out for the parts and the wiring, '
      + 'File ▸ Image Save and File ▸ Print for a picture. All of those are '
      + 'copies sent out; none of them is where the design lives.\n\n'
      + 'The first and the third need you signed in. Save As needs nothing but '
      + 'a browser.\n\n'
      + 'Saving is also automatic: a second and a half after the last change '
      + 'the design is written without being asked, and the cloud mark beside '
      + 'the Save button says where that has got to — waiting, saving, saved, or '
      + 'failed. Pressing Save yourself is never wrong; it is usually already '
      + 'done.\n\n'
      + 'Every save keeps the state it replaced, fifty deep. Un-Do walks back '
      + 'into those once this session\'s own undo is spent, so closing a drawing '
      + 'no longer draws a line under everything done to it.',
  },
  {
    code: 'FF',
    title: 'Save to Project',
    tip: 'Make a new project from what is open and keep the design on the web, in your account.',
    body:
      'Makes a new project from whatever is open and saves the design to the '
      + 'web — your account — under a name you choose. It is what File ▸ Save '
      + 'does the first time, before there is a project to save into.\n\n'
      + '1. Press F F.\n'
      + '2. Type a name. You are offered the name of the first sheet, or the '
      + 'board, or the text file, whichever there is.\n'
      + '3. Press Enter.\n\n'
      + 'From then on File ▸ Save writes into it, and so does the automatic '
      + 'save.\n\n'
      + 'Use it a second time to fork what is open into another project under a '
      + 'new name, leaving the first as it was.\n\n'
      + 'Text files count: a netlist you have corrected, or a note written '
      + 'against a job, goes into the project like anything else.',
  },
  {
    code: 'FD',
    title: 'Import Project',
    tip: 'Open a design saved in your account — or, with CIRCAD file commands off, a file from disk.',
    body:
      'Lists the designs saved in your account and opens one.\n\n'
      + '1. Press F D.\n'
      + '2. Pick the project. Everything in it opens: every sheet, the board, '
      + 'and any text files saved with it.\n\n'
      + 'These letters and File ▸ Open swap over with the "CIRCAD file commands" '
      + 'preference. It is on by default, so Open takes a file from disk and '
      + 'this lists your projects; turn it off in Setup ▸ Preferences and the '
      + 'two exchange places. Nothing else about either changes.\n\n'
      + 'Reading a file — whichever door you come through — understands our own '
      + '.sircad projects, CIRCAD schematics and boards, Altium, and KiCad. Ours '
      + 'comes back exactly as it went out; the rest are translations and say '
      + 'so, with anything that has no equivalent here reported rather than '
      + 'quietly dropped. Parts are copied into your personal library on the way '
      + 'past, so a symbol that arrived with somebody else\'s design is there to '
      + 'place in your own.\n\n'
      + 'A file written by a newer SirCAD than this one is refused rather than '
      + 'opened short — reading it here would drop whatever this build does not '
      + 'understand, and the next save would write that loss back over it.\n\n'
      + 'Unsaved work is never thrown away without asking.',
  },
  {
    code: 'FE',
    title: 'New',
    tip: 'Start a fresh, blank design — an empty schematic and an empty board.',
    body:
      'Starts a fresh, blank design: an empty schematic and an empty board, '
      + 'belonging to no project.\n\n'
      + 'This is not what the original does with these letters. There, File ▸ '
      + 'Erase deletes the open file from the disk. Nothing here erases '
      + 'anything, and if there is unsaved work you are asked before it is put '
      + 'aside.\n\n'
      + 'Both documents are cleared, not only the one on show. Save writes the '
      + 'whole design, so a blank sheet left paired with a stale board would be '
      + 'saved together and surprise you later.\n\n'
      + 'Also: File ▸ New Text File opens an empty text document beside the '
      + 'drawings. File ▸ New Sheet adds a sheet to the schematic you already '
      + 'have.',
  },
  {
    code: 'Fi',
    title: 'New Sheet',
    tip: 'Add a sheet to the schematic. A design is one board and any number of sheets.',
    body:
      'Adds a sheet to the schematic.\n\n'
      + 'A design here is one board and any number of sheets, saved and opened '
      + 'together as one project. The original used these letters for a sibling '
      + 'window instead; there are no sibling windows here, because the tabs do '
      + 'that job.\n\n'
      + '1. Press F i.\n'
      + '2. Name the sheet.\n\n'
      + 'File ▸ Next Sheet (F N) and File ▸ Previous Sheet (F V) step through '
      + 'them, and so do the tabs.\n\n'
      + 'Net Scan and the ratsnest read every sheet, so a net carried from one '
      + 'sheet to another by name is one net.',
  },
  {
    code: 'FN',
    title: 'Next Sheet',
    tip: 'Go to the next sheet of the schematic.',
    body:
      'Goes to the next sheet of the schematic.\n\n'
      + 'Also: File ▸ Previous Sheet (F V) goes back, and clicking a tab goes '
      + 'straight to the one you want, which is quicker once there are more than '
      + 'three or four. File ▸ New Sheet adds one.',
  },
  {
    code: 'FV',
    title: 'Previous Sheet',
    tip: 'Go to the previous sheet of the schematic.',
    body:
      'Goes to the previous sheet of the schematic.\n\n'
      + 'The original stepped to the previous file. Here it is the previous '
      + 'sheet of the design you already have open, because a design is one '
      + 'board and any number of sheets and they are opened and saved '
      + 'together.\n\n'
      + 'Also: File ▸ Next Sheet (F N) goes forward, and clicking a tab goes '
      + 'straight to the one you want, which is quicker once there are more '
      + 'than three or four.',
  },
  {
    code: 'FQ',
    title: 'Close All',
    tip: 'Put away every open drawing and text file at once.',
    body:
      'Puts away every open drawing and text file at once.\n\n'
      + 'Nothing is deleted. A project closed is still in your account, and '
      + 'File ▸ Import Project opens it again. Unsaved work is asked about '
      + 'first.\n\n'
      + 'This is not signing out — File ▸ Quit (F X) does that.',
  },
  {
    code: 'FX',
    title: 'Quit',
    tip: 'Sign out of your account.',
    body:
      'Signs you out.\n\n'
      + 'There is no window to close: the program is a page, and closing the '
      + 'tab does that. What this ends is the session with your account, which '
      + 'is worth doing on a machine that is not yours.\n\n'
      + 'If there is unsaved work you are asked first. Anything already saved '
      + 'is in your account and will be there when you sign in again, on this '
      + 'machine or another.\n\n'
      + 'Also: File ▸ Close All puts the drawings away without signing out.',
  },
  {
    code: 'FW',
    title: 'Rename',
    tip: 'Rename the document on show — this sheet, the board, or a text file.',
    body:
      'Renames whatever is in front: the sheet you are on, the board, or an '
      + 'open text file. This is the document\'s own name, not a path. Where a '
      + 'file lands on disk is decided by Save As; where a design sits on the '
      + 'web is decided by the project it belongs to.\n\n'
      + '1. Click the tab of the document you mean, so it is the one in front.\n'
      + '2. Press F W.\n'
      + '3. Type the new name and press Enter.\n\n'
      + 'Also: double-clicking a tab renames the document it stands for, which '
      + 'is the same command by another door. To rename the whole project '
      + 'rather than one document in it, use File ▸ Save to Project and give it '
      + 'the new name.\n\n'
      + 'Worth doing before you export: Gerber Out, BOM Out, Net List Out and '
      + 'Image Save all build their filenames from the project name, or from '
      + 'the document name when there is no project.',
  },
  {
    code: 'FG',
    title: 'Gerber Out',
    tip: 'Write the photoplot set for the board — one file per layer, plus drilling, zipped.',
    body:
      'Writes the photoplot set for the open board: one RS-274X file per layer, '
      + 'an Excellon drill file for the holes, and the lot zipped into one '
      + 'archive to send.\n\n'
      + '1. Bring the board to the front. There is nothing to plot from a '
      + 'schematic.\n'
      + '2. Press F G.\n'
      + '3. Check which layers are going out and how they are named.\n'
      + '4. Confirm. The archive is written under the project\'s name, and the '
      + 'status line says how many files went into it and how many holes were '
      + 'drilled.\n\n'
      + 'The lettering it plots is the lettering the screen draws, from the '
      + 'same font layout, so what arrives on the board is what you laid '
      + 'out.\n\n'
      + 'Also: File ▸ Gerber In reads a set back — including the one you have '
      + 'just written, which is the cheapest possible check before it is sent. '
      + 'Block ▸ Isolation Output writes toolpaths instead, for a board you are '
      + 'cutting rather than etching. File ▸ Export to KiCad writes the design '
      + 'for editing rather than for making.',
  },
  {
    code: 'FK',
    title: 'Export to KiCad',
    tip: 'Write the board or sheet as a KiCad file, for anyone working in KiCad.',
    body:
      'Writes the document in front as a KiCad file. A board goes out as '
      + '.kicad_pcb and a schematic sheet as .kicad_sch.\n\n'
      + 'KiCad keeps board and schematic in separate files exactly as this '
      + 'does, so the command exports the one document in front rather than '
      + 'bundling a project. A design of a board and three sheets is four '
      + 'exports.\n\n'
      + '1. Click the tab of the board or sheet you want.\n'
      + '2. Press F K.\n'
      + '3. Say where it goes. Where the browser offers a real save dialog you '
      + 'choose the folder and the name; otherwise it arrives in your downloads '
      + 'under a name taken from the project.\n\n'
      + 'Also: File ▸ Gerber Out writes the fabrication set a board house '
      + 'wants, which is what to send if the recipient is making the board '
      + 'rather than editing it. File ▸ Save As writes SirCAD\'s own format, '
      + 'which is the only one that loses nothing.\n\n'
      + 'Every export is a translation, and whatever the other program has no '
      + 'place for is left behind. Keep the SirCAD file as the original and '
      + 'treat the export as a copy sent out.',
  },
  {
    code: 'FJ',
    title: 'Export to CIRCAD',
    tip: 'Write the document in front as a CIRCAD .SCH or .PCB, for the original program.',
    body:
      'Writes the document in front in the original\'s own format — a .SCH for a '
      + 'sheet, a .PCB for a board — so it can be opened in CIRCAD.\n\n'
      + '1. Click the tab of the board or sheet you want.\n'
      + '2. Press F J.\n'
      + '3. Name the file. It arrives in your downloads.\n\n'
      + 'One document, not the project: that is all the format holds. A design '
      + 'of a board and three sheets is four exports.\n\n'
      + 'It is a translation, and the status line says so every time. The format '
      + 'belongs to another program and has no field for a pad\'s real shape, '
      + 'for a mirrored string, or for a footprint name — measured across the '
      + 'sample designs, a round trip through it drops several thousand values. '
      + 'That is the right trade when the recipient is CIRCAD and the wrong one '
      + 'for keeping your own copy.\n\n'
      + 'Also: File ▸ Save As writes the whole design as a .sircad file and '
      + 'loses nothing, which is the copy to keep. Naming a file .SCH or .PCB '
      + 'there does the same as this command. File ▸ Export to KiCad and File ▸ '
      + 'Gerber Out are the other two ways out, and both are translations in the '
      + 'same sense.',
  },
  {
    code: 'FH',
    title: 'Gerber In / View',
    tip: 'Open a set of photoplot files, look at them, and import them as copper if you want to.',
    body:
      'Opens a set of Gerber files — the artwork a board house is sent — and '
      + 'shows it.\n\n'
      + 'The viewer comes first and importing is a button on it. A heap of '
      + 'Gerbers is identifiable only by convention: the filenames say which '
      + 'layer is which, and the conventions differ between the programs that '
      + 'wrote them. Looking at a set someone has sent you is a good enough '
      + 'reason on its own.\n\n'
      + '1. Press F H.\n'
      + '2. Choose the files. A whole set at once is normal — one per layer, '
      + 'plus a drill file.\n'
      + '3. Check what each layer turned out to be. The viewer names them and '
      + 'lets you turn each on and off.\n'
      + '4. If you want it as a drawing, import it. It becomes a board document '
      + 'you can edit like any other.\n\n'
      + 'What you get is shapes: copper, mask, silk and holes. There are no '
      + 'nets, no components and no schematic behind them, so Net Scan and the '
      + 'ratsnest have nothing to work from until you give them something. This '
      + 'is the right way to recover a board that exists only as artwork. It is '
      + 'not a way back to the design that produced it.\n\n'
      + 'Also: File ▸ Import Project reads Altium, KiCad and CIRCAD design '
      + 'files, which do carry the nets and the parts.',
  },
  {
    code: 'FM',
    title: 'Image Save',
    tip: 'Write what is on screen as a PNG, at twice screen resolution.',
    body:
      'Writes what is on screen as a PNG, at twice screen resolution, to your '
      + 'downloads.\n\n'
      + 'For a quotation, a report, an email, or a note to yourself. It is a '
      + 'picture and nothing more — no layers, no nets, nothing anybody can '
      + 'import.\n\n'
      + 'The name is taken from the project, or from the document when there is '
      + 'no project, so File ▸ Rename first if the name matters.\n\n'
      + 'Also: File ▸ Print sends the same plot to paper, or to a PDF, through '
      + 'the browser\'s own print dialog. File ▸ Gerber Out is what a board '
      + 'house wants; a picture is not.',
  },

  // --- Grab -------------------------------------------------------------------
  {
    code: 'GW',
    title: 'Multiline',
    tip: 'Grab a whole run of segments rather than the single straight piece under the cursor.',
    body:
      'Grabs a whole run — every straight piece joined end to end with the one '
      + 'you click — instead of the single record under the cursor.\n\n'
      + 'The file stores a track that turns four corners as five separate '
      + 'straight lines. That is right for the format and wrong for the way you '
      + 'think about it: to you it is one track. Grab ▸ Line takes the piece you '
      + 'clicked. This takes the run.\n\n'
      + '1. Press G W.\n'
      + '2. Click anywhere along the run.\n'
      + '3. Move it, turn it, mirror it, or press Delete. Escape puts it back '
      + 'where it was.\n\n'
      + 'Also: Delete ▸ Multi Line removes a whole run in one click without '
      + 'grabbing it first, and Grab ▸ Vertex takes a single corner when what '
      + 'you want is to move the bend rather than the track.',
  },

  // --- Edit -------------------------------------------------------------------
  {
    code: 'Ed',
    title: 'Replace Next',
    tip: 'Replace the next match and move on, one keypress at a time.',
    body:
      'Replaces the next match with the text you gave Replace, then finds the '
      + 'one after it. One keypress per replacement, so you see each one happen '
      + 'and can stop the moment it goes wrong.\n\n'
      + 'The three work as a set:\n\n'
      + '1. E r — Replace — asks what to find and what to put in its place, and '
      + 'does the first one.\n'
      + '2. E d — Replace Next — does the next. Press it again for the one after '
      + 'that.\n'
      + '3. E 8 — Replace All — does every one that is left, without asking '
      + 'again.\n\n'
      + 'These work in a text file: a netlist, a BOM, a note, a source file. '
      + 'For the strings on a drawing use Edit ▸ Text, which opens the one you '
      + 'click.\n\n'
      + 'Also: the editor has its own find bar on Ctrl+F, which is the faster '
      + 'way in once a text file is open. Un-Do reverses a run of replacements '
      + 'as one step.',
  },
  {
    code: 'Ea',
    title: 'Align',
    tip: 'Scroll two open text files together, so the same place in each sits on the same line.',
    body:
      'Lines two text files up beside each other and scrolls them together, so '
      + 'that the same place in each sits on the same row of the screen.\n\n'
      + 'It does no comparing. It reads both files, works out which lines '
      + 'correspond, and pads the shorter side with blanks where the other has '
      + 'lines it does not — then leaves you to read. That is the difference '
      + 'between this and Edit ▸ Compare, which steps you to each difference in '
      + 'turn and selects it.\n\n'
      + '1. Open both text files, so both are on tabs.\n'
      + '2. Press E H.\n'
      + '3. Choose which two, if more than two are open.\n\n'
      + 'The original needed the two files in side-by-side sibling windows '
      + 'arranged by hand. Here they are picked from what is open.\n\n'
      + 'Use it on two netlists, or two revisions of one, when what you want is '
      + 'to read them against each other rather than to be told what changed.',
  },
  {
    code: 'Ep',
    title: 'Compare',
    tip: 'Step through what differs between two open text files, a difference at a time.',
    body:
      'Compares two text files line by line and takes you to each difference '
      + 'in turn, selecting the run that differs in both files at once.\n\n'
      + 'This is the one that answers "what changed". Edit ▸ Align is its quiet '
      + 'half: it lines the two up and says nothing.\n\n'
      + '1. Open both text files, so both are on tabs.\n'
      + '2. Press E I.\n'
      + '3. Choose which two, if more than two are open.\n'
      + '4. Step to the next difference, and the next.\n\n'
      + 'A run of lines added on one side and removed on the other is shown as '
      + 'one change rather than two, which is what it usually is.\n\n'
      + 'The original needed the two files in side-by-side sibling windows. '
      + 'Here they are picked from what is open.\n\n'
      + 'The most common use is two netlists — the one the schematic produces '
      + 'and the one the board produces — where the differences are the errors.',
  },

  // --- Views ------------------------------------------------------------------
  {
    code: 'VB',
    title: 'Board View',
    tip: 'Show the board flat, as it would be made: copper, mask and silk in their real colours.',
    body:
      'Shows the board as the finished article, seen flat from above: copper '
      + 'under solder mask, silkscreen over it, pads bare where the mask is '
      + 'opened, holes drilled through.\n\n'
      + 'This is for judging rather than for editing. Silk running over a pad, '
      + 'a legend too small to read, a sliver of mask between two pins — all '
      + 'obvious here and invisible on the drawing, where every layer is a line '
      + 'of its own colour.\n\n'
      + '1. Bring the board to the front. There is nothing to show from a '
      + 'schematic.\n'
      + '2. Press V B.\n'
      + '3. Turn the board over to see the underside.\n'
      + '4. Escape, or Back, returns you to the drawing.\n\n'
      + 'Underside lettering reads backwards on the drawing, because there you '
      + 'are looking down through the board. Here it reads the right way round, '
      + 'because here you have turned the board over.\n\n'
      + 'Also: Views ▸ Render shows the same board in the round. Setup ▸ Mirror '
      + 'View flips the drawing itself, which is what you want while working on '
      + 'the underside rather than while looking at it.',
  },
  {
    code: 'VR',
    title: 'Render',
    tip: 'Show the board in the round, as the finished article.',
    body:
      'Draws the board in three dimensions — substrate, copper, mask, silk and '
      + 'the parts on it — and lets you turn it in your hands.\n\n'
      + '1. Bring the board to the front.\n'
      + '2. Press V R.\n'
      + '3. Drag to turn it. Escape, or Back, returns you to the drawing.\n\n'
      + 'What it is good for is the check no flat view gives: whether a tall '
      + 'part sits over a connector, whether the board is the shape you meant, '
      + 'whether the silkscreen tells the person assembling it what they need '
      + 'to know.\n\n'
      + 'Also: Views ▸ Board View is the same board flat, which is the better '
      + 'view for reading fine detail. File ▸ Image Save writes what is on '
      + 'screen as a picture, for a quotation or a report.',
  },
  {
    code: 'VP',
    title: 'Pin Numbers',
    tip: 'Write each pad’s pin number across it, to check a footprint against its part.',
    body:
      'Turns pin numbers on and off over the pads.\n\n'
      + 'A footprint is only right if pad 1 is the pin the datasheet calls 1, '
      + 'and nothing else on the board says so. With this on, every pad carries '
      + 'its number and a footprint can be read against the datasheet in one '
      + 'pass.\n\n'
      + 'Press V P to turn it on, V P again to turn it off. The numbers are a '
      + 'way of looking at the board rather than part of it: never plotted, '
      + 'never printed, never saved.\n\n'
      + 'Also: Edit ▸ Pad opens one pad and shows its number along with '
      + 'everything else about it. Nets ▸ Highlight Net colours everything on '
      + 'one net, which answers the neighbouring question of what a pad is '
      + 'joined to.',
  },
  {
    code: 'VI',
    title: 'Photo Underlay',
    tip: 'List the photographs under this drawing, and what can be done to them.',
    body:
      'Lists the photographs laid under this drawing and gives you the '
      + 'controls for each: move it, scale it, turn it, fade it, take it '
      + 'away.\n\n'
      + 'A photograph is for tracing from. Scan or photograph a board you have '
      + 'in your hand, lay it under the drawing, scale it until it is life '
      + 'size, and draw the board over the top of it.\n\n'
      + 'The whole sequence:\n\n'
      + '1. V L — Load Image — choose the picture.\n'
      + '2. V S — Scale Image — click two features whose real distance apart you '
      + 'know, and say what that distance is. This is what makes it life size.\n'
      + '3. V M — Move Image — click a feature on the photograph, then the place '
      + 'that feature belongs.\n'
      + '4. V N — Nudge Image — arrows, for the last thousandth of an inch.\n'
      + '5. V F — Fade Image — until what you draw stands out against it.\n'
      + '6. Draw the board.\n'
      + '7. V D — Drop Image — when you no longer need it.\n\n'
      + 'A photograph is loaded fresh each session and never written into the '
      + 'file. The file remembers that there was one and where it sat, so '
      + 'loading the same picture again puts it back where you left it.\n\n'
      + 'Also: Zoom ▸ Absolute sets the view so one pixel of the picture is one '
      + 'pixel on screen, which is the honest magnification to trace at.',
  },
  {
    code: 'VL',
    title: 'Load Image',
    tip: 'Lay a photograph of a board under the drawing to trace over.',
    body:
      'Lays a picture under the drawing: a scan, a photograph, a screenshot of '
      + 'somebody else\'s artwork.\n\n'
      + '1. Press V L.\n'
      + '2. Choose the picture. It appears at the middle of the view.\n'
      + '3. Scale it — Views ▸ Scale Image — before you draw anything, or you '
      + 'will be tracing a board that is the wrong size.\n\n'
      + 'The picture is never saved into the file. The file keeps a note of '
      + 'which picture it was and where it sat, and loading the same one again '
      + 'restores that, so a design does not carry a photograph around inside '
      + 'it forever.\n\n'
      + 'Also: Views ▸ Photo Underlay lists everything already laid down and '
      + 'reaches all the same controls from one dialog.',
  },
  {
    code: 'VM',
    title: 'Move Image',
    tip: 'Move the photograph: click a feature on it, then where that feature belongs.',
    body:
      'Slides the photograph without changing its size or its angle.\n\n'
      + '1. Press V M.\n'
      + '2. Click a feature on the photograph — a mounting hole, a corner, pin 1 '
      + 'of a connector.\n'
      + '3. Click the place that feature belongs on the drawing.\n\n'
      + 'The photograph moves by the distance between the two clicks, so the '
      + 'accuracy of the move is the accuracy of your two clicks. Zoom in '
      + 'first: a feature clicked at a fitted view is worth a tenth of the same '
      + 'feature clicked close up.\n\n'
      + 'Also: Views ▸ Nudge Image moves it by the arrow keys instead, which is '
      + 'how to close the last thousandth once it is nearly right.',
  },
  {
    code: 'VS',
    title: 'Scale Image',
    tip: 'Set the photograph to scale from two features whose real distance apart you know.',
    body:
      'Makes the photograph life size, from a distance you know.\n\n'
      + '1. Press V S.\n'
      + '2. Click the first of two features whose real distance apart you '
      + 'know.\n'
      + '3. Click the second.\n'
      + '4. Type what that distance really is, in the units the drawing is '
      + 'working in, and press Enter.\n\n'
      + 'Choose the two as far apart as the picture allows, and pick features '
      + 'you can be exact about: two mounting holes across the board, the first '
      + 'and last pin of a long connector. An error in the click is divided by '
      + 'the distance between them, so a long span forgives a sloppy click and '
      + 'a short one does not.\n\n'
      + 'A header on 0.1 inch centres is the surveyor\'s friend here: ten '
      + 'pitches is exactly one inch, and it is printed on the board you are '
      + 'copying.\n\n'
      + 'Also: Views ▸ Corner Image sizes it roughly by eye, which is enough to '
      + 'get started. Views ▸ Nudge Image stretches by the arrow keys with '
      + 'Shift held, for a correction too small to click.',
  },
  {
    code: 'VC',
    title: 'Corner Image',
    tip: 'Size the photograph roughly by saying where its far corner goes.',
    body:
      'Sizes the photograph by eye: you say where its far corner should land '
      + 'and it is scaled to reach.\n\n'
      + '1. Press V C.\n'
      + '2. Click where the top-right corner of the picture belongs.\n\n'
      + 'The picture keeps its proportions — whichever of the two directions is '
      + 'the tighter fit decides the scale — so it is never stretched into '
      + 'something that no longer matches the board.\n\n'
      + 'This is the rough-and-ready one, for getting a picture near enough to '
      + 'work with. Views ▸ Scale Image is the exact one, and is what to use '
      + 'before tracing anything you intend to have made.',
  },
  {
    code: 'VN',
    title: 'Nudge Image',
    tip: 'Line the photograph up by keyboard: arrows move it, Shift stretches it, comma and full stop turn it.',
    body:
      'Lines the photograph up by keyboard, for the corrections too small to '
      + 'click. The numbers are shown as they change.\n\n'
      + 'Press V N to start, then:\n\n'
      + '· Arrows — move it one step. With Shift they stretch it along that '
      + 'axis instead, about its insertion point.\n'
      + '· Comma and full stop — turn it by a tenth of a degree. With Shift, a '
      + 'hundredth.\n'
      + '· Square brackets — scale both directions together by one per cent. '
      + 'With Shift, a tenth of one per cent.\n'
      + '· Enter or Escape — finish. Both keep what you did; a nudge has '
      + 'nothing half-finished about it.\n\n'
      + 'Un-Do reverses the whole session of nudging as one step, so there is '
      + 'no risk in trying something.\n\n'
      + 'A scan is rarely square to the paper, and a tenth of a degree over '
      + 'three inches is five thousandths of an inch at the far end — which is '
      + 'why the turn here is in tenths rather than whole degrees.',
  },
  {
    code: 'VT',
    title: 'Turn Image',
    tip: 'Turn the photograph a quarter turn.',
    body:
      'Turns the photograph ninety degrees. Press it again for the next '
      + 'quarter.\n\n'
      + 'This is for a picture that arrived on its side — a scan taken '
      + 'landscape of a board that is portrait. For the fraction of a degree a '
      + 'scan is out of square, use Views ▸ Nudge Image, which turns in tenths '
      + 'and hundredths.',
  },
  {
    code: 'VF',
    title: 'Fade Image',
    tip: 'How strongly the photograph shows through, so what you trace stays the thing you see.',
    body:
      'Steps the photograph\'s strength through 20, 40, 60, 80 and 100 per '
      + 'cent and back round again. One keypress each.\n\n'
      + 'It is the control you reach for most while tracing, which is why it is '
      + 'two keys and not a slider: strong enough to see the feature you are '
      + 'about to draw, faint enough that what you have already drawn stands '
      + 'out from it. The right answer changes every few minutes, and it is '
      + 'different for silk than for copper.',
  },
  {
    code: 'VD',
    title: 'Drop Image',
    tip: 'Take the photograph away, and the reference to it.',
    body:
      'Removes the photograph from the drawing, and the note the file keeps of '
      + 'it.\n\n'
      + 'Do this when the tracing is done. Nothing you have drawn is touched — '
      + 'the picture was only ever underneath.\n\n'
      + 'Un-Do brings it back if you were not finished with it. Otherwise '
      + 'Views ▸ Load Image lays it down again from the file it came from, and '
      + 'you scale it afresh.',
  },

  // --- Setup ------------------------------------------------------------------
  {
    code: 'SR',
    title: 'Record Macro',
    tip: 'F11 — record what you do once, clicks and all, and do it again with F12.',
    body:
      'Records what you do, and plays it back. Keys and mouse clicks, in the '
      + 'order you made them.\n\n'
      + '1. Press F11, or S R. The status line says it is recording.\n'
      + '2. Do the thing once.\n'
      + '3. Press F11 (or S R) again to stop. It says what it caught.\n'
      + '4. Press F12, or S B, wherever you want it done again.\n\n'
      + 'A note on the function keys: F11 and F12 belong to the browser before '
      + 'they belong to any page — F11 is full screen and F12 opens the '
      + 'developer tools — and a browser is entitled to keep them. Where yours '
      + 'does, S R and S B do the same two things and always reach us. They are '
      + 'the reliable route; the function keys are the quick one.\n\n'
      + 'Clicks are remembered as offsets, not as places. The first click of the '
      + 'recording is the anchor, and everything after it keeps its distance '
      + 'from that — so playing back stamps the whole sequence down wherever the '
      + 'cursor is now. A macro that repeated itself into the exact spot it was '
      + 'recorded would put the second copy on top of the first, which is a '
      + 'recording nobody would play twice.\n\n'
      + 'One macro. Recording again replaces it. It is kept with your '
      + 'preferences rather than in the drawing, because a way of working '
      + 'belongs to you and not to one board.\n\n'
      + 'What it does not record: anything typed into a dialog, and the keys '
      + 'that work the recorder — including the S and the R when you stop it '
      + 'that way, which are taken back out. A macro that finished by starting '
      + 'another recording would not be one you played twice. Playback stops if '
      + 'a command puts a dialog up — the macro cannot know what to answer, and '
      + 'pressing on would let the next recorded key answer it by accident.\n\n'
      + 'Also: Setup ▸ Key Stroke Macros is the original\'s own kind — a written '
      + 'list of command codes on a function key. That one cannot capture where '
      + 'you clicked; this one cannot be typed out by hand. They sit beside each '
      + 'other because they are good at opposite things.',
  },
  {
    code: 'SB',
    title: 'Play Macro',
    tip: 'F12 — do the recorded macro again, anchored at the cursor.',
    body:
      'Plays the macro recorded with F11 or S R, anchored where the cursor is. '
      + 'F12 and S B both do it — and where the browser keeps F12 for its own '
      + 'developer tools, S B is the one that always reaches us.\n\n'
      + 'The first click of the recording lands under the pointer and every '
      + 'later one keeps its offset from it, so the same sequence can be '
      + 'stamped down anywhere. A macro of nothing but keys ignores the cursor '
      + 'altogether.\n\n'
      + 'It stops if a dialog opens, and says at which step. A macro cannot '
      + 'answer a question, and carrying on would let whatever it recorded next '
      + 'answer it by accident.\n\n'
      + 'Un-Do reverses what a macro did the same as anything else — each '
      + 'command it played is a step of its own, so a macro of six commands is '
      + 'six presses of Un-Do rather than one.',
  },
  {
    code: 'SN',
    title: 'Snap Grid',
    tip: 'Hold points to the grid, or let them go where you put them.',
    body:
      'Turns grid snap on and off. With it on, every point you place lands on '
      + 'the nearest grid position; with it off, points go exactly where the '
      + 'cursor is.\n\n'
      + 'Pads and line ends still snap either way. That is deliberate, and it '
      + 'is what makes the setting safe to turn off: a wire dropped near a pin '
      + 'still joins it, so turning snap off to squeeze a track between two '
      + 'pads cannot quietly break a connection.\n\n'
      + 'Press S N to change it. The status bar shows which it is.\n\n'
      + 'Also: Setup ▸ Grid Snaps steps the grid through its sizes — the usual '
      + 'answer when a point will not go where you want is a finer grid rather '
      + 'than no grid. Setup ▸ Zero Point moves the origin the grid is measured '
      + 'from, which is what to do when the board\'s own datum is not at zero.',
  },
  {
    code: 'SU',
    title: 'Preferences',
    tip: 'Settings that follow you between designs, rather than belonging to the file.',
    body:
      'Holds the settings that are about you rather than about the drawing. '
      + 'They follow you between designs, where Setup ▸ File Parameters belongs '
      + 'to the file and travels with it.\n\n'
      + 'The ones worth knowing:\n\n'
      + '· CIRCAD file commands — whether File ▸ Open takes a file from disk '
      + 'and Import lists your projects, or the other way round. On by default, '
      + 'because in the original Open has meant a file on disk for twenty years '
      + 'and that is what the fingers expect.\n'
      + '· Pick only on the active layer — whether a click takes whatever is '
      + 'under it on any visible layer, or only what is on the layer you are '
      + 'working on. Turn it on to grab a track from beneath a poured plane.\n'
      + '· Fit layers on opening a CIRCAD file — whether a design read from the '
      + 'original has its layers mapped onto this program\'s, or kept exactly '
      + 'as it wrote them.\n'
      + '· Theme — dark or light.\n\n'
      + 'The original showed a raw two-column list of every internal parameter '
      + 'and warned you off most of them. This shows the ones that are actually '
      + 'yours to set.\n\n'
      + 'Also: Setup ▸ Options holds the display switches, which change what is '
      + 'drawn rather than how the program behaves.',
  },
  {
    code: 'SF',
    title: 'Library Files',
    tip: 'Open the part library: the symbols and footprints available to place.',
    body:
      'Opens the part library — the symbols and footprints you can place.\n\n'
      + 'Parts arrive in it three ways: the libraries that come with the '
      + 'program, anything you draw and declare with Place ▸ Build Component, '
      + 'and everything that came in with a design you imported. A symbol '
      + 'somebody else drew is yours to place once you have opened their '
      + 'design.\n\n'
      + '1. Press S F.\n'
      + '2. Search, or read down the list.\n'
      + '3. Choose a part. It comes onto the cursor; click to put it down.\n\n'
      + 'The original\'s command chose which library files this drawing could '
      + 'reach. Here there is one library, yours, and this is the way into '
      + 'it.\n\n'
      + 'Also: Place ▸ Component opens the picker over the drawing, which is '
      + 'quicker when you already know what you want.',
  },

  // --- Nets -------------------------------------------------------------------
  {
    code: 'NO',
    title: 'Net List Out',
    tip: 'Write the netlist as a text file on its own tab, in the original’s pipe-delimited form.',
    body:
      'Opens the netlist as a text file on its own tab, in the original\'s '
      + 'pipe-delimited form.\n\n'
      + 'It is taken from the schematic where there is one and from the '
      + 'board\'s own connectivity where there is not. The first line of the '
      + 'file says which, so a netlist is never ambiguous about where it came '
      + 'from.\n\n'
      + '1. Press N O.\n'
      + '2. Read it. Correct it, if it wants correcting.\n'
      + '3. File ▸ Save As writes it out on its own; File ▸ Save keeps it in '
      + 'the project alongside the design.\n\n'
      + 'It opens in the editor rather than dropping into your downloads '
      + 'because it is a file you will want to read before anyone acts on '
      + 'it.\n\n'
      + 'Nets with one pin on them are left out: a wire that reaches nothing is '
      + 'not a connection, and listing it would bury the ones that matter.\n\n'
      + 'Also: Nets ▸ Net Scan finds what is actually joined on the board, '
      + 'which is the question a netlist cannot answer. Edit ▸ Compare against '
      + 'a netlist taken from the schematic is how the two are checked against '
      + 'each other.',
  },

  {
    code: 'ND',
    title: 'DFM Check',
    tip: 'Can this board be made? Drill sizes, annular rings, clearances and the board edge.',
    body:
      'Checks whether the board can be manufactured — as against whether the '
      + 'circuit is right, which is what Nets ▸ Net Scan is for. Those are the '
      + 'two ways a finished board fails, and they fail for unrelated reasons: a '
      + 'design can be electrically perfect and impossible to build.\n\n'
      + '1. Bring the board to the front.\n'
      + '2. Press N D.\n'
      + '3. Read down the report. Clicking a row goes to the fault on the board '
      + 'and selects it.\n'
      + '4. Change the set at the top if your shop can do better, or worse, than '
      + 'the standard one.\n\n'
      + 'What it measures:\n\n'
      + '· Board outline — whether there is an edge to cut the board out of at '
      + 'all. Without one there is no board, only a drawing, and no shop can '
      + 'quote it.\n'
      + '· Track width, and clearance between copper on different nets.\n'
      + '· Drill sizes, smallest and largest.\n'
      + '· Annular ring — the copper left round a hole once it is drilled. A '
      + 'drill a few thousandths off centre breaks a thin one, and that is the '
      + 'commonest reason a board comes back not working.\n'
      + '· Hole to hole, and copper to the board edge.\n'
      + '· Silkscreen line width and lettering height.\n\n'
      + 'The report is grouped by rule rather than listed flat, worst first. One '
      + 'footprint with a tight ring breaks the same rule forty times, and forty '
      + 'rows of it would bury the one thing that actually stops the board being '
      + 'made.\n\n'
      + 'It says what passed as well as what failed. A report that lists only '
      + 'failures leaves you guessing whether the rest was checked or merely '
      + 'never looked at, and a rule that could not run — copper to the edge, on '
      + 'a board with no edge — says so rather than passing quietly.\n\n'
      + 'Nothing is changed. There is more than one answer to a tight annular '
      + 'ring — a smaller drill, a bigger pad, or moving the part — and only you '
      + 'know which.\n\n'
      + 'The Rules button holds the nine figures, with named sets to start '
      + 'from. What you set is saved onto the board, so a design carries the '
      + 'rules it was checked against wherever it goes.\n\n'
      + 'Your own shop\'s numbers are worth typing once:\n\n'
      + '1. Rules… — start from whichever set is closest.\n'
      + '2. Change whatever your shop told you is different.\n'
      + '3. Save as… — give it their name.\n\n'
      + 'A saved set is offered on every board from then on, marked "(saved)" '
      + 'to tell it from the ones that ship. It goes with your preferences '
      + 'rather than with the design, because you send every board to the same '
      + 'two or three places.\n\n'
      + 'Delete removes one. It is offered only for a set of yours — the ones '
      + 'that ship cannot be deleted, and changing a figure stops Delete '
      + 'offering to remove a set the numbers no longer are.',
  },

  // --- Help -------------------------------------------------------------------
  {
    code: '?H',
    title: 'Help',
    tip: 'Search this help file: every command, what it is for, and how to work it.',
    body:
      'Opens this help file — every command the program has, what it is for, '
      + 'and the steps to work it.\n\n'
      + '1. Press ? H.\n'
      + '2. Type into the search box, or read down the index on the left. The '
      + 'index is grouped by menu; a search is ranked, best answer first.\n'
      + '3. Arrow keys move down the results without leaving the box.\n\n'
      + 'Search reads titles, command codes and the text of every topic. A '
      + 'title match ranks above a passing mention, so searching "flood" gives '
      + 'you Flood Fill before the commands that merely mention flooding. '
      + 'Typing a command code — F S, Z W — goes straight to that topic. Every '
      + 'word you type has to appear somewhere, so adding a word narrows the '
      + 'answer rather than widening it.\n\n'
      + 'Only commands this program has are listed. An index full of things '
      + 'that do nothing is a list of disappointments.\n\n'
      + 'Also: hovering a menu entry shows its one-line description without '
      + 'opening anything.',
  },
  {
    code: '?R',
    title: 'Support',
    tip: 'Report a problem, with the drawings that were open attached to it.',
    body:
      'Sends a problem report.\n\n'
      + '1. Press ? R.\n'
      + '2. Describe what happened, and what you expected instead.\n'
      + '3. Send.\n\n'
      + 'The drawings that were open go with it, along with the build number, '
      + 'so the fault can be reproduced rather than guessed at. That is the '
      + 'whole point of reporting it from inside the program rather than by '
      + 'email.\n\n'
      + 'You need to be signed in, since a report belongs to an account.\n\n'
      + 'Also: Help ▸ Reports (? B) is the other end — it shows what you have '
      + 'sent and what has been said in reply.',
  },
  {
    code: '?B',
    title: 'Reports',
    tip: 'Read the replies to problems reported from Help ▸ Support.',
    body:
      'Shows the problem reports sent from this account, and the replies to '
      + 'them.\n\n'
      + '1. Press ? B.\n'
      + '2. Pick a report to read it, with whatever was attached and whatever '
      + 'has been said since.\n\n'
      + 'Help ▸ Support (? R) is the other end of this.\n\n'
      + 'Both need you signed in, since a report belongs to an account.',
  },
  {
    code: '?I',
    title: 'Install as App',
    tip: 'Give SirCAD its own window and its own icon, off the browser tab.',
    body:
      'Installs SirCAD as an application. It then opens in a window of its own '
      + '— no address bar, no tabs, no browser menus over the drawing — with an '
      + 'icon in your applications and task switcher like any other program.\n\n'
      + '1. Press ? I, or use the Install app button in the title bar.\n'
      + '2. Confirm the name your browser suggests.\n'
      + '3. SirCAD opens in its own window. It is on your machine from then on.\n\n'
      + 'Nothing moves and nothing is copied. It is the same program reading '
      + 'the same account, so every project, preference and recorded macro is '
      + 'exactly where you left it. Signing in is not repeated.\n\n'
      + 'The offer only appears in an ordinary browser tab, and only once the '
      + 'browser is satisfied the app can be installed — that needs a secure '
      + '(https) address, and on a first visit it can take a few moments or a '
      + 'second visit. In the installed window the command is not on the menu '
      + 'at all, there being nothing left to install.\n\n'
      + 'By browser: Chrome and Edge can be asked outright, which is what this '
      + 'command does. Safari can install but only from its own menu — File ▸ '
      + 'Add to Dock on a Mac, Share ▸ Add to Home Screen on an iPad or iPhone '
      + '— so there this command shows you where that is rather than pretending '
      + 'to do it. Firefox on the desktop cannot install web apps at all, and '
      + 'says so instead of sending you looking.\n\n'
      + 'To remove it, uninstall it as you would any application; on Chrome and '
      + 'Edge there is also an Uninstall in the app window\'s own menu. Removing '
      + 'it takes nothing with it — the work is in the account and in this '
      + 'browser, not in the installed window.\n\n'
      + 'A side effect worth knowing: installing also puts the program itself '
      + 'in this browser\'s cache, so it starts without waiting for the network '
      + 'and opens even when there is none. Saving to the account still needs a '
      + 'connection, and the cloud on the title bar says whether it has one.',
  },
];

/**
 * Paragraphs added to the end of an extracted topic.
 *
 * Keyed by the original's code. The original's words stay first and are not
 * touched — they say what the command is for, and that has not changed. What
 * follows says what is different here, and it is written only where something
 * *is* different: a note that restates the paragraph above it makes the help
 * longer and no better.
 */
const NOTES: Record<string, string> = {
  // --- File -------------------------------------------------------------------
  FO:
    'On these letters SirCAD reaches for a design file on disk — one of our own '
    + '.sircad projects, a CIRCAD .SCH or .PCB, an Altium board, a KiCad file — '
    + 'and File ▸ Import Project lists the designs saved in your account.\n\n'
    + 'A .sircad file opens whole: the board, every sheet and any text files, '
    + 'exactly as they went in. The rest are translations and open as much as '
    + 'their format carried.\n\n'
    + 'The two swap over with the "CIRCAD file commands" preference, which is '
    + 'on by default. Turn it off in Setup ▸ Preferences and Open lists your '
    + 'projects instead.\n\n'
    + 'Opening a design brings in everything it holds: every sheet, the board, '
    + 'and the parts. Parts are copied into your personal library on the way '
    + 'past, so a symbol that arrived with somebody else\'s design is there to '
    + 'place in your own.\n\n'
    + 'Also: File ▸ Gerber In reads a set of photoplot files, for a board that '
    + 'exists only as artwork.',

  FA:
    'Save As writes the design to a file on this machine, in SirCAD\'s own '
    + 'format — a `.sircad` file. Nothing is lost in it, so this is the copy to '
    + 'keep.\n\n'
    + '1. Press F A.\n'
    + '2. Choose the folder and the name. Where the browser supports it this is '
    + 'the real system save dialog; where it does not, you are asked for a name '
    + 'and the file arrives in your downloads.\n\n'
    + 'A `.sircad` file holds the whole project: the board, every sheet, and any '
    + 'text files open beside them. Open reads it back exactly as it went in.\n\n'
    + 'Name the file `.SCH` or `.PCB` instead and it exports to CIRCAD, which is '
    + 'a different thing and says so when it happens. That format belongs to '
    + 'another program and has no field for a great deal this one knows — a '
    + 'pad\'s real shape, whether a string is mirrored, a footprint name — and '
    + 'it carries one document rather than a project. Use it to hand a drawing '
    + 'to CIRCAD, not to keep your own copy.\n\n'
    + 'With a text file in front it writes the text file instead.\n\n'
    + 'Also: File ▸ Save keeps the whole design in your account, which is the '
    + 'ordinary way to work here. Use Save As to hand a file to somebody, to '
    + 'keep a copy outside the account, or when you are not signed in. File ▸ '
    + 'Export to KiCad and File ▸ Gerber Out are the other two ways out, and '
    + 'both are translations in the same sense.',

  FC:
    'Close puts away the document in front and leaves the rest of the design '
    + 'open. Nothing is deleted: a project closed is still in your account, and '
    + 'the tab bar keeps a way back to a tab closed by mistake.\n\n'
    + 'Also: File ▸ Close All (F Q) puts away everything at once.',

  Ft:
    'A text file here is a document like any other, on a tab beside the '
    + 'drawings. It is saved into the project with everything else, it has its '
    + 'own find and replace, and it is coloured according to what it turns out '
    + 'to be — a netlist, a BOM, PHP, JavaScript, and the rest.\n\n'
    + 'Also: Edit ▸ Align and Edit ▸ Compare work on two open text files at '
    + 'once, which is how to see what changed between two netlists.',

  FB:
    'The BOM opens as a text file on its own tab, in CSV, which is what a '
    + 'purchasing spreadsheet wants. It opens in the editor rather than '
    + 'dropping straight into your downloads because it is a file you will want '
    + 'to read, and probably to correct, before anybody orders from it. File ▸ '
    + 'Save As writes it out when you are happy with it.\n\n'
    + 'Identical parts are one line: the same part, value and footprint is the '
    + 'same thing to buy, however many the board carries. A quad gate placed as '
    + 'four gates is one chip — U1a to U1d count as U1, once.\n\n'
    + 'A part with no reference designator is left out, on the grounds that it '
    + 'is a graphic rather than something to buy. If a real part is missing '
    + 'from the list, give it a designator.',

  FP:
    'Print hands the plot to the browser\'s own print dialog, which is also the '
    + 'way to a PDF: choose "Save as PDF" as the destination.\n\n'
    + 'What prints is the plot, not the page — the panels, the menu bar and the '
    + 'dark desktop have no business on paper.\n\n'
    + 'Also: File ▸ Image Save writes a PNG, which is easier to paste into '
    + 'something else.',

  // --- Place ------------------------------------------------------------------
  PW:
    'On a schematic this is the main command: it is how a sheet gets wired. It '
    + 'is held to the horizontal and the vertical and cannot run diagonally at '
    + 'all, whatever the ortho mode is set to, because that is what a signal '
    + 'wire is.\n\n'
    + '1. Press P W. Chosen from the menu it asks for the layer and width '
    + 'first; typed, it starts straight away with what is already set.\n'
    + '2. Click, or press Space, to start the run.\n'
    + '3. Click at each corner.\n'
    + '4. Double-click, or press Enter, to end the run. Escape abandons it.\n\n'
    + 'Dropping a wire on a pin joins it. A junction dot appears where two '
    + 'wires meet and are meant to be joined.\n\n'
    + 'It is a schematic command and does nothing on a board. Copper is laid '
    + 'with Place ▸ Line.',

  PL:
    'This is the command that lays copper. On a board it draws on the working '
    + 'layer, so a track goes down on whichever copper layer is active, and on '
    + 'a schematic it draws a plain line that is not a wire and joins '
    + 'nothing.\n\n'
    + '1. Press P L. From the menu it asks for the layer, width and style '
    + 'first; typed, it starts with what is already set.\n'
    + '2. Click, or press Space, to start the run.\n'
    + '3. Click at each corner.\n'
    + '4. Double-click, or press Enter, to end the run. Escape abandons it.\n\n'
    + 'To change sides in the middle of a run, press Place ▸ Via (P V) or use '
    + 'the + and − keys: a via is dropped at the corner you are on and the run '
    + 'carries on from there on the new layer. Only the part after the via '
    + 'changes sides — what you have already drawn stays where it was.\n\n'
    + 'It is not held to any direction. Setup ▸ Ortho Mode decides that; Place '
    + '▸ Ortho-Line is the one that is always held to the eight directions, and '
    + 'Place ▸ Signal Wire the one always held to two.',

  PP:
    'The same command makes a pad, a via and a mounting hole — they differ only '
    + 'in their parameters, which is why there is one command and not three. A '
    + 'hole with no copper round it is a mounting hole; a small round pad on '
    + 'every copper layer is a via.\n\n'
    + '1. Press P P. From the menu it asks for the shape, the sizes and the '
    + 'hole first; typed, it uses what is already set.\n'
    + '2. Click where the pad goes. Keep clicking for more of the same.\n'
    + '3. Escape when done.\n\n'
    + 'Also: Place ▸ Via (P V) has its own parameters and is the quicker route '
    + 'while routing — and pressed during a run it breaks the run for a via '
    + 'rather than starting a fresh one.',

  // --- Edit -------------------------------------------------------------------
  ES:
    'Item Snap moves the cursor onto the nearest point worth being exactly on: '
    + 'the end of a line, the centre of a pad, a corner.\n\n'
    + 'It is what to press when a click has to be exact and the grid is not '
    + 'helping — connecting to a pad that is off-grid, or picking up a vertex '
    + 'in a crowded corner.\n\n'
    + 'Also: Setup ▸ Snap Grid holds every point to the grid, which is the '
    + 'other half of the same problem. Pads and line ends snap even with the '
    + 'grid off, so a wire dropped near a pin joins it either way.',

  Ef:
    'Find works in a text file — a netlist, a BOM, a note, a source file. Not '
    + 'on a drawing: for the strings on a drawing use Edit ▸ Text, which opens '
    + 'the one you click.\n\n'
    + 'The editor has its own find bar on Ctrl+F, which is the faster way in '
    + 'once a text file is open.\n\n'
    + 'Also: E 5 finds the next match, E r replaces, E d replaces the next one '
    + 'and moves on, and E 8 replaces the rest in one go.',

  // --- Un-Do ------------------------------------------------------------------
  UU:
    'Un-Do here reaches further back than the session.\n\n'
    + 'When this window\'s own undo is spent, it carries on into the states '
    + 'kept on the server: every save keeps the state it replaced, fifty deep, '
    + 'so work done in a previous session is still there to be stepped back '
    + 'through. Closing a drawing no longer draws a line under everything done '
    + 'to it.\n\n'
    + 'The two are separate stacks and the deeper one is only entered when the '
    + 'nearer one is empty. Un-Do ▸ Redo walks forward through both the same '
    + 'way.\n\n'
    + 'This needs the design to be saved in a project — there is nowhere to '
    + 'keep the older states otherwise.',

  UR:
    'Redo walks forward through this session\'s undo stack, and past it into '
    + 'the saved states on the server, exactly as Un-Do walks back through '
    + 'them.',

  // --- Grab -------------------------------------------------------------------
  //
  // The original lists spirals and ellipses among what these grips take hold
  // of. Here they take arcs and circles, and a bulged line keeps its curvature
  // in a field rather than on a handle — so each says which, instead of leaving
  // it to be discovered by clicking at a line and having nothing happen.
  GG:
    'Here it takes hold of arcs and circles. A bulged line is curved by the '
    + 'Bulge field in Edit ▸ Line, which is where that shape keeps its bend.\n\n'
    + '1. Press G G.\n'
    + '2. Click on the edge of the curve — not its middle, which is empty.\n'
    + '3. Move it, and click again to set it down. Escape puts it back.\n\n'
    + 'Also: Grab ▸ Arc Edge (G A) moves the same curve but takes hold where you '
    + 'clicked. This one takes hold of the centre, which is what you want when '
    + 'the centre has to land on a pad or a grid point.',

  GD:
    'Here it takes hold of arcs and circles. A bulged line is curved by the '
    + 'Bulge field in Edit ▸ Line.\n\n'
    + '1. Press G D.\n'
    + '2. Click on the edge, nearer the end you mean — that is the one it takes.\n'
    + '3. Swing it. The far end stays where it is. Click to set it.\n\n'
    + 'The radius follows the cursor as well as the angle, so the end goes where '
    + 'you put it rather than sliding along a circle it cannot leave.',

  GV:
    'Here it takes hold of arcs and circles. A bulged line is curved by the '
    + 'Bulge field in Edit ▸ Line.\n\n'
    + '1. Press G V.\n'
    + '2. Click on the edge of the curve.\n'
    + '3. Move the centre. Both ends stay exactly where they are, so what '
    + 'changes is how much the curve bows between them.\n\n'
    + 'The centre cannot go just anywhere. A circle through two fixed points has '
    + 'its centre on the line halfway between them and nowhere else, so the '
    + 'cursor is dropped onto that line — which is why the curve bows rather '
    + 'than slides when you push sideways.',

  // --- Un-Do ------------------------------------------------------------------
  UA:
    'It walks back a step at a time rather than jumping to the oldest state, so '
    + 'everything it undoes lands on the Re-Do stack and Un-Do ▸ Redo All puts '
    + 'it all back.\n\n'
    + 'It stops at this session’s own stack. Un-Do on its own carries past that '
    + 'into the states kept with each save, and "undo everything" meaning "and '
    + 'then every save this design has ever had" is not what anybody presses it '
    + 'for.',

  US:
    'Saving does not purge it here. The original cleared the Un-Do stack when a '
    + 'file was written to disk; this keeps the stack, and keeps the state '
    + 'before each save as well, so Un-Do reaches back through both. That makes '
    + 'purging a decision rather than something that happens to you, which is '
    + 'why it is worth having as a command.\n\n'
    + 'It clears this window’s stack only. The saved states belong to the '
    + 'project rather than to the window, and are left alone.',

  // --- Setup ------------------------------------------------------------------
  ST:
    'The four modes are named in the status bar rather than drawn as a symbol: '
    + 'off, H/V, 45°, and H/V/45°.\n\n'
    + 'This setting is the one a general line is drawn in. Some commands carry '
    + 'their own and ignore it: Place ▸ Signal Wire is always horizontal and '
    + 'vertical, because that is what a signal wire is, and Place ▸ Ortho-Line '
    + 'is always all eight directions. Place ▸ Line is the free one.',

  SO:
    'Options holds the display and mouse switches — what is drawn and how, and '
    + 'what the buttons do — as against Setup ▸ Preferences, which holds how '
    + 'the program behaves, and Setup ▸ File Parameters, which belongs to the '
    + 'file and travels with it.\n\n'
    + 'What is on this page:\n\n'
    + '· Animated block perimeter — the marked block drawn with a moving '
    + 'dashed outline, or a plain one.\n'
    + '· Thick cross-hair cursor — three pixels rather than one. A single '
    + 'pixel at half opacity disappears into copper.\n'
    + '· Zoom in and out with the mouse buttons — left zooms in, right zooms '
    + 'out. This is the switch to turn off if the view moves when you did not '
    + 'ask it to. It is on by default, as the original has it.\n\n'
    + 'Mouse-button zoom only ever acts where no command wants the click: with '
    + 'anything in hand, any command waiting for a point, or a Grab or Edit '
    + 'looking for its target, the click goes to the command and the view '
    + 'stays put. A click that lands on an object picks it up, so only bare '
    + 'board zooms.\n\n'
    + 'Changes here show on the drawing the instant they are made, so a '
    + 'setting can be judged on the drawing rather than on its own wording. '
    + 'Cancel puts back whatever was in force when the page opened.',

  SM:
    'File Parameters belongs to the file and travels with it: units, grid, the '
    + 'draw modes for lines, pads and text, and the rest of what a drawing '
    + 'carries about itself.\n\n'
    + 'Also: Setup ▸ Options holds the display switches that are yours rather '
    + 'than the file\'s, and Setup ▸ Preferences the ones that follow you '
    + 'between designs.',

  // --- Nets -------------------------------------------------------------------
  NS:
    'Net Scan follows the copper: it starts where you click and lights up every '
    + 'track, pad and via in electrical contact with it, however many corners '
    + 'and vias that takes.\n\n'
    + '1. Press N S.\n'
    + '2. Click anywhere on the net.\n\n'
    + 'This is the question a netlist cannot answer. A netlist says what should '
    + 'be joined; this says what *is*. Two nets shorted by a sliver of copper '
    + 'show up here as one, and nowhere else.\n\n'
    + 'Also: Nets ▸ Net Scan Plus and Minus step to the next net and the '
    + 'previous. Nets ▸ Clear Highlights puts the colours back. Nets ▸ Net Scan '
    + 'Log does the whole board at once and reports it.',

  NR:
    'The ratsnest is drawn from the netlist over the board, so it needs a board '
    + 'and a netlist to work from — it does nothing on a schematic, where '
    + 'everything is already drawn as wires.\n\n'
    + 'Nets ▸ Ratsnest (lite) is the same picture with the longest connections '
    + 'left out, which on a dense board is the difference between a guide and a '
    + 'haystack. Nets ▸ Un-Ratsnest turns it off and removes any ratlines that '
    + 'were drawn onto the layer as well.',

  NH:
    'Highlight Net colours a whole net so you can see where it goes.\n\n'
    + '1. Press N H.\n'
    + '2. Click a pad or a track on the net, or type the net\'s name.\n\n'
    + 'Once highlighted, Nets ▸ Name Net renames the lot and Delete ▸ '
    + 'Highlighted Items removes it. Nets ▸ Clear Highlights puts the colours '
    + 'back.\n\n'
    + 'Also: Nets ▸ Net Scan follows the copper rather than the name, which is '
    + 'what to use when the question is what is really joined to what.',

  NN:
    '1. Nets ▸ Highlight Net, or Nets ▸ Net Scan, to light up the net first.\n'
    + '2. Press N N.\n'
    + '3. Type the name and press Enter.\n\n'
    + 'Every highlighted pad and track takes the name. Naming nets is what '
    + 'makes a netlist readable and what lets a net be carried from one sheet '
    + 'to another, since two nets with the same name on different sheets are '
    + 'one net.',

  // --- Zoom -------------------------------------------------------------------
  ZL:
    'The + and − keys step through the layers without opening anything, which '
    + 'is the usual way to change sides while routing.\n\n'
    + 'On a board, doing that in the middle of a run puts a via in: the run '
    + 'breaks at the corner you are on, a via is dropped there, and the rest of '
    + 'the track carries on on the new layer. What you have already drawn stays '
    + 'on the layer you drew it on.',

  ZS:
    '1. Press Z S.\n'
    + '2. Type the number of pixels per inch you want — the same number the '
    + 'status bar is showing.\n'
    + '3. Press Enter.\n\n'
    + 'The view zooms about the cursor, or about the middle of the window when '
    + 'the cursor is not over the drawing. There are limits at both ends, and '
    + 'asking for more than the view will give lands on the nearest it will go '
    + 'and says so.\n\n'
    + 'Reach for it when two drawings have to be compared at the same size, or '
    + 'when a screenshot has to come out at a stated scale.',

  ZA:
    'It works on the photograph under the cursor, or on the last one laid down '
    + 'if the cursor is not over one.\n\n'
    + 'This is the view to trace at. Closer is guessing at detail the scan does '
    + 'not hold; further away is throwing away detail it does.\n\n'
    + 'Also: Views ▸ Photo Underlay lists the pictures and reaches everything '
    + 'that can be done to them.',

  // --- Help -------------------------------------------------------------------
  '?A':
    'It shows the build number, which is what a problem report needs: a fault '
    + 'can then be tied to the version it came from.\n\n'
    + 'Also: Help ▸ Support sends a report, with the build number and the open '
    + 'drawings attached to it.',
};

/**
 * The original's topics with ours merged in.
 *
 * A note is appended to the extracted body; a whole topic replaces the
 * extracted one, which is how a command whose letters mean something else here
 * avoids being described as the original's command.
 */
export const ALL_TOPICS: HelpTopic[] = (() => {
  const byCode = new Map<string, HelpTopic>();
  for (const t of HELP_TOPICS) {
    const note = NOTES[t.code];
    byCode.set(t.code, note ? { ...t, body: `${t.body}\n\n${note}` } : t);
  }
  for (const t of SIRCAD_TOPICS) byCode.set(t.code, t);
  return [...byCode.values()].sort((a, b) => a.code.localeCompare(b.code));
})();

/** Help for one command code, ours or the original's. */
export function topicFor(code: string): HelpTopic | undefined {
  return ALL_TOPICS.find((t) => t.code === code);
}
